import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, Alert } from 'react-bootstrap';
import {
	TableOptions,
	useBlockLayout,
	useFilters,
	usePagination,
	useRowSelect,
	useSortBy,
	useTable,
} from 'react-table';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import Btn from '../Btn/Btn';
import VehicleFilters from './components/VehicleFilters';
import VehicleTable from './components/VehicleTable';
import VehiclePagination from './components/VehiclePagination';
import TableSkeleton from '../TableSkeleton/TableSkeleton';
import type { TableSkeletonProps } from '../../types/index';
import type { FilterOption } from '../../vehicleList/filterOptions';
import { PAGE_SIZE_OPTIONS } from '../../vehicleList/params';
import type { VehicleFilterState } from '../../vehicleList/params';
import './VehicleTable.scss';

interface PaginatedVehicleTableProps {
	columns: any;
	data: any[];
	loading?: boolean;
	error?: any;
	onRetry?: () => void;

	totalCount: number;
	pageIndex: number;
	pageSize: number;
	onPageChange: (pageIndex: number) => void;
	onPageSizeChange: (pageSize: number) => void;
	onSortChange: (sortBy: { id: string; desc: boolean }[]) => void;

	filterIntialState: VehicleFilterState;
	onFiltersChange: (filters: VehicleFilterState) => void;
	filterOptions?: Record<string, FilterOption[]>;
	presetFilter?: any;

	hiddenColumns?: string[];
	handleDoubleClick?: (row: any) => void;
	skeletonProps?: TableSkeletonProps;
	viewLimitMessage?: string;
	/** Riceve i filtri applicati, non la bozza dei campi. */
	EmptyComponent?: React.FC<{ filterState: any }>;
	toggleVisibilityModified?: (id: string) => void;
	onPageRowsChange?: (ids: string[]) => void;
}

const PaginatedVehicleTable: React.FC<PaginatedVehicleTableProps> = ({
	columns,
	data,
	loading,
	error,
	onRetry,
	totalCount,
	pageIndex,
	pageSize,
	onPageChange,
	onPageSizeChange,
	onSortChange,
	filterIntialState,
	onFiltersChange,
	filterOptions,
	presetFilter,
	hiddenColumns = [],
	handleDoubleClick,
	skeletonProps,
	viewLimitMessage,
	EmptyComponent,
	toggleVisibilityModified,
	onPageRowsChange,
}) => {
	const { t } = useTranslation();
	const pageCount = Math.ceil(totalCount / pageSize) || 0;

	const tableOptions: TableOptions<Object> = {
		columns,
		data,
		manualPagination: true,
		manualFilters: true,
		manualSortBy: true,
		pageCount,
		autoResetPage: false,
		autoResetSortBy: false,
		autoResetFilters: false,
		initialState: { pageSize, hiddenColumns },
		...(toggleVisibilityModified
			? { toggleRow: toggleVisibilityModified }
			: {}),
	};

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		state,
		page,
		rows,
		canPreviousPage,
		canNextPage,
		pageOptions,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		toggleAllRowsSelected,
	} = useTable(
		tableOptions,
		useFilters,
		useSortBy,
		usePagination,
		useBlockLayout,
		useRowSelect
	);

	useEffect(() => {
		if (pageIndex !== state.pageIndex) gotoPage(pageIndex);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageIndex]);

	useEffect(() => {
		if (pageIndex !== state.pageIndex) onPageChange(state.pageIndex);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.pageIndex]);

	useEffect(() => {
		if (pageSize !== state.pageSize) onPageSizeChange(state.pageSize);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.pageSize]);

	useEffect(() => {
		onSortChange(state.sortBy as any);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.sortBy]);

	const pageRowIds = useMemo(
		() => rows.map((row: any) => row.original.id),
		[rows]
	);

	const pageRowKey = pageRowIds.join(',');

	useEffect(() => {
		onPageRowsChange?.(pageRowIds);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageRowKey]);

	// Cio' che la lista sta mostrando: VehicleFilters lo notifica al mount
	// (filtri salvati) e a ogni applicazione.
	const [appliedFilters, setAppliedFilters] = useState<VehicleFilterState>();

	const handleFiltersChange = useCallback(
		(filters: VehicleFilterState) => {
			toggleAllRowsSelected(false);
			setAppliedFilters(filters);
			onFiltersChange(filters);
		},
		[toggleAllRowsSelected, onFiltersChange]
	);

	const showSkeleton = loading && !error && !rows.length;
	const showEmptyState = !loading && !error && !rows.length && EmptyComponent;

	return (
		<>
			<Accordion className="mt-4" flush>
				<VehicleFilters
					headerGroups={headerGroups}
					setAllFilters={_.noop}
					loading={loading}
					presetFilter={presetFilter}
					filterIntialState={filterIntialState}
					onFiltersChange={handleFiltersChange}
					filterOptions={filterOptions}
					showSearch
					manualApply
				/>
			</Accordion>

			{viewLimitMessage ? (
				<Alert variant="warning" className="my-4">
					<div>{viewLimitMessage}</div>
				</Alert>
			) : null}

			{showSkeleton ? (
				<TableSkeleton {...skeletonProps} />
			) : showEmptyState ? (
				<EmptyComponent filterState={appliedFilters} />
			) : (
				<div style={{ opacity: loading ? 0.6 : 1 }}>
					<VehicleTable
						getTableProps={getTableProps}
						headerGroups={headerGroups}
						getTableBodyProps={getTableBodyProps}
						page={page}
						prepareRow={prepareRow}
						resultCount={totalCount}
						viewLimitMessage={viewLimitMessage}
						handleDoubleClick={handleDoubleClick}
					/>
				</div>
			)}

			{totalCount > 0 && !showSkeleton && (
				<div style={{ pointerEvents: loading ? 'none' : 'all' }}>
					<VehiclePagination
						gotoPage={gotoPage}
						canPreviousPage={canPreviousPage}
						previousPage={previousPage}
						nextPage={nextPage}
						canNextPage={canNextPage}
						pageCount={pageCount}
						state={state}
						pageOptions={pageOptions}
						setPageSize={setPageSize}
						pageSizeOptions={PAGE_SIZE_OPTIONS}
					/>
				</div>
			)}

			{error && (
				<div className="table-error-message">
					<span>{error.message}</span>
					{onRetry && (
						<Btn
							className="d-inline-block"
							size="sm"
							onClick={onRetry}
						>
							{t('retry', { defaultValue: 'Riprova' })}
						</Btn>
					)}
				</div>
			)}
		</>
	);
};

export default PaginatedVehicleTable;
